import { useRef, useContext, useEffect } from "react";
import { Typography, TextField, Box, Stack, FormControlLabel, FormControl, Radio, RadioGroup, FormLabel } from "@mui/material";
import { ToolContext } from "../../../../../core/context/ToolContext";
import { rqx } from "../../../../../core/request/API";
import { env } from "../../../../../core/Env";
import { SnakeLoader } from "../../../../../core/loader/SnakeLoader";
import { AnotherDetails } from "./AnotherDetails";
import ReactQuill from "react-quill";

export const MainInformation = ({ handleChange }) => {
    const { realproperty_state } = useContext(ToolContext);
    const { loader, types, mainInfrmtn, dfltMainInfrmtn } = realproperty_state;
    const quillRef = useRef(null);
    // syp = sellYourProperty
    const handleDescriptionChange = (value) => {
        const t = document.createElement("div");
        t.innerHTML = value.replace(/(<([^>]+)>)/gi, "");
        const plainText = t.textContent.trim();
        if (plainText.length > 900) {
            quillRef.current.getEditor().root.innerHTML = mainInfrmtn.data.description; 
            return; 
        }

        mainInfrmtn.set({
            ...mainInfrmtn.data,
                description: plainText.length === 0 ? "" : value,
        });
    };
    // to exclude html tags
    const getPlainTextLength = (v) => {
        const t = document.createElement("div");
        t.innerHTML = v.replace(/(<([^>]+)>)/gi, "");
        return t.textContent.length;
    };
    //to reset sub-sub types when user changing offer type & property type
    const toRst = () => {
        return {
            bathroom: "",
            bedroom: "",
            blockLotUnitFloorNo: "",
            buildingName: "",
            condominiumName: "", 
            floorArea: "",
            forClosure: 0,
            landSize: "",
            preSelling: 0,
            showPrice: 0,
            subdivisionName: "",
            unitNoFloorNo: "",
            price: { ...mainInfrmtn.data.price, value: "", stat: false, msg: dfltMainInfrmtn.data.price.msg },
            monthly: { ...mainInfrmtn.data.monthly, value: "", stat: false, msg: dfltMainInfrmtn.data.monthly.msg },
            yearly: { ...mainInfrmtn.data.yearly, value: "", stat: false, msg: dfltMainInfrmtn.data.yearly.msg },
            availableFrom: { ...mainInfrmtn.data.availableFrom, value: "", stat: false, msg: dfltMainInfrmtn.data.availableFrom.msg },
        }
    }

    const readTypes = async () => {
        if (types.data.propertyTypes.length === 0) {
            const res_property_type = await rqx.get(`<<tool>>/${env()}/propertyListing/initialReader`, { ref: "property_type" });
            if (res_property_type) {
                types.set((p) => ({
                    ...p, propertyTypes: res_property_type
                }))
            }
        }
    };

    const readSubTypes = async (property_id) => {
        const res_property_subtype = await rqx.get(`<<tool>>/${env()}/propertyListing/initialReader`, { ref: "property_subtype", filtered_id: property_id });
        if (res_property_subtype) {
            types.set((p) => ({
                ...p,
                propertySubTypes: res_property_subtype
            }));
        }
    }
    // to auto convert into int the id, bc in forms it reads and convert it into str automatically
    const handleStrToIntChange = async (e) => {
        const { name, value } = e.target;
        mainInfrmtn.set({
            ...mainInfrmtn.data,
                [name]: name === "offerType" || name === "propertyType" || name === "subType" || name === "ownerType"? {
                    ...mainInfrmtn.data[name],
                    value: value ? parseInt(value) : '',
                    stat: false
                } : value ? parseInt(value) : ''
        });
        if (name === "propertyType" || name === "offerType") {
            loader.set(false);
            if (Number(value)) {
                name === "propertyType" && await readSubTypes(Number(value));
                mainInfrmtn.set((p) => ({
                    ...p, 
                    ...toRst(),
                    ...(name === "propertyType" && { subType: {...mainInfrmtn.data.subType, value: '', stat: true, msg: "Sub-type is required" } }),
                }));
            }
            loader.set(true);
            return;
        }
    }

    useEffect(() => {
        readTypes();
        readSubTypes(mainInfrmtn.data.propertyType.value)
        // eslint-disable-next-line
    }, [])

    return (
        <>  
            <Box mb="1rem">
                <Typography mb="0.5rem">
                    Listing Title
                    <Typography component="span" color="error">*</Typography>
                </Typography>
                <TextField name="listingTitle" value={mainInfrmtn.data.listingTitle.value} onChange={(e) => handleChange(e, mainInfrmtn)} error={mainInfrmtn.data.listingTitle.stat} fullWidth variant="outlined" size="small" sx={{ bgcolor: "#fff" }}/>
            </Box>
            <Box mb="1rem">
                <Typography mb="0.5rem">
                    Description
                </Typography>
                <Box>
                    <Box sx={{ height: "13.75rem", overflow: "hidden"}}>
                        <ReactQuill
                            ref={quillRef}
                            value={mainInfrmtn.data.description}
                            onChange={handleDescriptionChange}
                            placeholder="Enter description here..."
                            modules={{
                                toolbar: [
                                    ["bold", "italic"],
                                    [{ list: "ordered" }, { list: "bullet" }],
                                ],
                            }}
                            style={{
                                backgroundColor: "#ffffff",
                                borderRadius: "5px",
                                border: "1px solid rgba(0,0,0,0.2)",
                                height: "100%",
                                paddingBottom: "2.678rem",
                            }}
                        />
                    </Box>
                    <Stack direction="row" justifyContent="right">
                        <Typography component="span">{getPlainTextLength(mainInfrmtn.data.description)}</Typography>
                        <Typography component="span">/900 characters</Typography>
                    </Stack>
                </Box>
            </Box>
            <Box mb="1rem" display="flex" sx={{ flexDirection: "row" }}>
                <FormControl fullWidth>
                    <FormLabel id="i-am-a-group-label" sx={{ color: "#000", "&.Mui-focused": { color: "#000"}, }}>
                        I am a <Typography component="span" color="error">*</Typography>
                    </FormLabel>
                    <RadioGroup row aria-labelledby="i-am-a-group-label" name="ownerTypeRadioGroup" value={mainInfrmtn.data.ownerType.value} onChange={handleStrToIntChange}>
                        <FormControlLabel name="ownerType" value={2} control={<Radio />} label="Property Owner" />
                        <FormControlLabel name="ownerType" value={3} control={<Radio />} label="Broker" />
                    </RadioGroup>
                </FormControl>
                <FormControl fullWidth>
                    <FormLabel id="offer-type-group-label" sx={{ color: "#000", "&.Mui-focused": { color: "#000"}, }}>
                        Offer type <Typography component="span" color="error">*</Typography>
                    </FormLabel>
                    <RadioGroup row aria-labelledby="offer-type-group-label" name="offerTypeRadioGroup" value={mainInfrmtn.data.offerType.value} onChange={handleStrToIntChange}>
                        <FormControlLabel name="offerType" value={1} control={<Radio/>} label="Sell" />
                        <FormControlLabel name="offerType" value={2} control={<Radio/>} label="Rent" />
                    </RadioGroup>
                </FormControl>
            </Box>
            {types.data.propertyTypes.length !== 0 && 
                <Box mb="1rem">
                    <FormControl fullWidth>
                        <FormLabel id="property-type-group-label" sx={{ color: "#000", "&.Mui-focused": { color: "#000"}, }}>
                            Property type <Typography component="span" color="error">*</Typography>
                        </FormLabel>
                        <RadioGroup row aria-labelledby="property-type-group-label" name="propertyTypeRadioGroup" value={mainInfrmtn.data.propertyType.value} onChange={handleStrToIntChange}>
                            {types.data.propertyTypes?.map((v, i) => (
                                <FormControlLabel name="propertyType" key={i} value={v.id} control={<Radio/>} label={v.name} />
                            ))}
                        </RadioGroup>
                    </FormControl>
                </Box>
            }
            { mainInfrmtn.data.propertyType && 
                <Box mb="1rem">
                    <FormControl fullWidth>
                        <FormLabel id="sub-type-group-label" sx={{ color: "#000", "&.Mui-focused": { color: "#000"}, }}>
                            Sub type <Typography component="span" color="error">*</Typography>
                        </FormLabel>
                        {types.data.propertySubTypes.length === 0 ?
                            <Box py="0.5rem" display="flex" alignItems="center" justifyContent="center">
                                <SnakeLoader bg="#11783C" size="7px" distance="7px"/> 
                            </Box> 
                        :
                            <RadioGroup row aria-labelledby="sub-type-group-label" name="subTypeRadioGroup" value={mainInfrmtn.data.subType.value} onChange={handleStrToIntChange}>
                                {types.data.propertySubTypes?.map((v, i) => (
                                    <FormControlLabel name="subType" key={i} value={v.id} control={<Radio/>} label={v.name} />
                                ))}
                            </RadioGroup>
                        }
                    </FormControl>
                </Box>
            }
            <AnotherDetails handleChange={handleChange} handleStrToIntChange={handleStrToIntChange}/>
        </>
    )
}
